import clsx from "clsx";
import { Check, Ellipsis, Link2, Lock, Maximize2, Minimize2, RotateCcw, X } from "lucide-react";
import { type MouseEvent as ReactMouseEvent, type PointerEvent as ReactPointerEvent, type ReactNode, useCallback, useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { type ConfirmOptions, ConfirmProvider, useConfirm } from "./Confirm";
import { MenuItem, Popover } from "./ui";
import { WindowLayer } from "./WindowPopup";
import { SaveArea, SaveIndicator, useSaveSummary } from "./saving";
import { useWithWindows } from "./ToolWindows";

/*
 * A window for one record (an item, a supplier...), like ServiceM8's job card: a blue title bar
 * (drag it to move the window; double-click to expand; it also shows how saves in the window are
 * going: saving.tsx), tabs down the left (its actions in a More menu at their foot),
 * and the tab's content, compact and edge to edge (the .record-window styles in styles.css).
 *
 * Like ServiceM8, it floats over the page without blocking it, and there's one of each kind:
 * tools open it from the page address, which holds one record of each kind, so opening another
 * replaces it, in the same place. Drag its right or bottom edge (or the corner) to resize it; each
 * kind remembers its size and place (the title bar's reset button puts it back to the start).
 * Escape closes the top one. On a phone it fills the screen
 * and the tabs run across the top.
 */

/** Title bar colours (styles.css .record-window.is-*). */
export type WindowTone = "blue" | "violet" | "teal" | "slate";

export interface WindowTab {
    id: string;
    label: string;
    icon: ReactNode;
    /** Has changes not saved yet: a dot on its icon. */
    unsaved?: boolean;
    /** What it's about is locked (e.g. a locked price): a small lock on its icon (bottom left), saying this (on hover, and to screen readers). */
    locked?: string;
    /** Something on it needs attention (e.g. a group's items, an item's cost to update): a small "!" on its icon (bottom right), red or amber, saying this (on hover, and to screen readers). */
    attention?: { danger: boolean; label: string };
}

/** Where a window is and how big, in pixels from the top left of the browser window. */
interface Geometry {
    left: number;
    top: number;
    width: number;
    height: number;
}

const MARGIN = 8;
const DEFAULT_SIZE = { width: 760, height: 560 };
const MIN_SIZE = { width: 440, height: 320 };

/** Each kind's last size and place, kept between visits (best effort: storage may be blocked). */
const storageKey = (kind: string) => `window:${kind}`;
function savedGeometry(kind: string): Geometry | null {
    try {
        const saved = JSON.parse(localStorage.getItem(storageKey(kind)) ?? "null") as Geometry | null;
        return saved && [saved.left, saved.top, saved.width, saved.height].every(Number.isFinite) ? saved : null;
    } catch {
        return null;
    }
}
function saveGeometry(kind: string, g: Geometry) {
    try {
        localStorage.setItem(storageKey(kind), JSON.stringify(g));
    } catch {
        // Not remembered this time.
    }
}
function forgetGeometry(kind: string) {
    try {
        localStorage.removeItem(storageKey(kind));
    } catch {
        // Nothing was remembered.
    }
}

/** How much of a window may be moved off the screen, at most (to see what's behind it): off the left, right or bottom, never the top. */
const OFF_SCREEN = 0.5;

/**
 * Fit a window on screen: no bigger than the browser window, no smaller than MIN_SIZE (if there's room), and at least half of it
 * visible (OFF_SCREEN), its title bar always: it can always be dragged back (and Escape closes it, if Close is off the screen).
 */
function fit(g: Geometry): Geometry {
    const maxWidth = globalThis.innerWidth - 2 * MARGIN;
    const maxHeight = globalThis.innerHeight - 2 * MARGIN;
    const width = Math.min(Math.max(g.width, MIN_SIZE.width), maxWidth);
    const height = Math.min(Math.max(g.height, MIN_SIZE.height), maxHeight);
    const offX = Math.round(width * OFF_SCREEN);
    const offY = Math.round(height * OFF_SCREEN);
    return {
        width,
        height,
        left: Math.min(Math.max(g.left, MARGIN - offX), globalThis.innerWidth - width - MARGIN + offX),
        top: Math.min(Math.max(g.top, MARGIN), globalThis.innerHeight - height - MARGIN + offY),
    };
}

/** A new kind of window opens in the middle of the screen; each other window open moves it down and across a little, so they don't sit exactly on top of each other. */
function firstGeometry(othersOpen: number): Geometry {
    const offset = 36 * othersOpen;
    const { width, height } = DEFAULT_SIZE;
    return fit({
        width,
        height,
        left: Math.round((globalThis.innerWidth - width) / 2) + offset,
        top: Math.max(MARGIN, Math.round((globalThis.innerHeight - height) / 2)) + offset,
    });
}

/**
 * The windows open now (by kind) and how high each sits: the latest opened or clicked is on top.
 * Escape closes only the top one.
 */
const stack = new Map<string, number>();
let nextZ = 40;
const FRONT_EVENT = "record-window:front";
const FLASH_EVENT = "record-window:flash";

/** Bring the open window of this kind (if any) to the front and focus it: e.g. after a link to the record it shows. */
export function bringToFront(kind: string) {
    globalThis.dispatchEvent(new CustomEvent(FRONT_EVENT, { detail: kind }));
}

/** Bring the open window of this kind to the front and flash it: it needs dealing with first (e.g. unsaved changes when another record was asked for). */
export function flashWindow(kind: string) {
    globalThis.dispatchEvent(new CustomEvent(FLASH_EVENT, { detail: kind }));
}

const isTop = (kind: string) => Math.max(...stack.values()) === stack.get(kind);

const isPhone = () => globalThis.matchMedia("(max-width: 767px)").matches;

/** What a drag on the window changes: its place (the title bar) or its size (an edge or the corner). */
type DragMode = "move" | "right" | "bottom" | "corner";

/** Dragging the title bar, an edge or the corner. Returns the pointer handlers for each, and the geometry. */
function useWindowGeometry(kind: string, othersOpen: number, enabled: boolean) {
    const [geometry, setGeometry] = useState<Geometry>(() => {
        const saved = savedGeometry(kind);
        return saved ? fit(saved) : firstGeometry(othersOpen);
    });
    const drag = useRef<{ mode: DragMode; x: number; y: number; start: Geometry } | null>(null);
    const latest = useRef(geometry);
    latest.current = geometry;

    // Stay on screen when the browser window gets smaller.
    useEffect(() => {
        const onResize = () => setGeometry((g) => fit(g));
        globalThis.addEventListener("resize", onResize);
        return () => globalThis.removeEventListener("resize", onResize);
    }, []);

    // Being moved (not resized): see-through, like ServiceM8's windows, to see what's behind. From the first movement, so a click doesn't flicker.
    const [moving, setMoving] = useState(false);
    const endDrag = (e: ReactPointerEvent<HTMLElement>) => {
        if (!drag.current) return;
        drag.current = null;
        setMoving(false);
        if (e.currentTarget.hasPointerCapture(e.pointerId)) e.currentTarget.releasePointerCapture(e.pointerId);
        saveGeometry(kind, latest.current);
    };
    const handlers = (mode: DragMode) => ({
        onPointerDown: (e: ReactPointerEvent<HTMLElement>) => {
            // Only the main button, and not from the title bar's buttons.
            if (!enabled || e.button !== 0 || (e.target as HTMLElement).closest("button")) return;
            e.preventDefault();
            e.currentTarget.setPointerCapture(e.pointerId);
            drag.current = { mode, x: e.clientX, y: e.clientY, start: latest.current };
        },
        onPointerMove: (e: ReactPointerEvent<HTMLElement>) => {
            const d = drag.current;
            if (!d) return;
            const dx = e.clientX - d.x;
            const dy = e.clientY - d.y;
            const s = d.start;
            if (d.mode === "move") {
                setMoving(true);
                setGeometry(fit({ ...s, left: s.left + dx, top: s.top + dy }));
            } else setGeometry(fit({ ...s, width: d.mode === "bottom" ? s.width : s.width + dx, height: d.mode === "right" ? s.height : s.height + dy }));
        },
        onPointerUp: endDrag,
        // The browser took the pointer back (e.g. a touch became a scroll): the drag's over all the same.
        onPointerCancel: endDrag,
    });
    /** Back to where and how big a new window of this kind starts, forgetting what was remembered. */
    const reset = () => {
        forgetGeometry(kind);
        setGeometry(firstGeometry(othersOpen));
    };
    return { geometry, handlers, reset, moving };
}

export function RecordWindow(
    props: Readonly<{
        /** What kind of record it's for ("item", "supplier"): one window of each kind can be open. */
        kind: string;
        /** Its title bar's colour, so kinds of window can be told apart (blue: ServiceM8's job card). */
        tone?: WindowTone;
        /** Before the title: what kind of record it is (alongside the colour, which not everyone can tell apart). */
        icon?: ReactNode;
        /** A Copy link button: a link to this page with just this window open (its record and tab), to send someone. */
        share?: boolean;
        title: ReactNode;
        /** The window's name for screen readers, when `title` isn't plain text. */
        label?: string;
        /** Next to the title, smaller (e.g. the item number). */
        subtitle?: ReactNode;
        tabs: WindowTab[];
        tab: string;
        onTab: (id: string) => void;
        /**
         * What can be done to the record (archive, remove from ServiceM8, delete...): in a More menu at
         * the foot of its tabs, each asked first (its `confirm`), in the window. Left out: not offered.
         */
        actions?: readonly (WindowAction | null | false | undefined)[];
        /** Notices about the whole record, above the tab: they stay put while the tab scrolls. */
        notices?: ReactNode;
        onClose: () => void;
        children: ReactNode;
    }>,
) {
    const dialog = useRef<HTMLDialogElement>(null);
    const [, navigate] = useLocation();
    const withWindows = useWithWindows();
    /**
     * A link in it to another page of the app (e.g. "$180.00 an hour" to Price List settings): the open windows go along, as the
     * sidebar's links take them (ToolWindows.tsx), so this one stays open, its unsaved changes with it.
     */
    const keepWindowsOpen = (e: ReactMouseEvent) => {
        if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        const link = (e.target as Element | null)?.closest?.("a[href]");
        if (!(link instanceof HTMLAnchorElement) || (link.target && link.target !== "_self") || link.hasAttribute("download")) return;
        const url = new URL(link.href, location.href);
        if (url.origin !== location.origin) return;
        e.preventDefault();
        // "~": the whole address, not one under the router it's in.
        navigate(`~${withWindows(url.pathname + url.search + url.hash)}`);
        // To a part of a page (#labour): said, as a browser does for its own # links (navigating doesn't), so a page already
        // showing can go to it again.
        if (url.hash) globalThis.dispatchEvent(new HashChangeEvent("hashchange"));
    };
    // The frame, once it's there, for popups inside the window to cover (WindowPopup.tsx).
    // Its body (under its title bar): where its popups and questions go (Confirm.tsx WindowLayer).
    const [layer, setLayer] = useState<HTMLElement | null>(null);
    const [expanded, setExpanded] = useState(false);
    // Copy link couldn't copy (e.g. blocked inside ServiceM8): the link, shown to copy by hand.
    const [showLink, setShowLink] = useState<string | null>(null);
    const [phone, setPhone] = useState(isPhone);
    const { kind, onClose } = props;
    // On the stack as soon as it's created, so a window opened at the same moment sees it.
    const [z, setZ] = useState(() => {
        const top = nextZ++;
        stack.set(kind, top);
        return top;
    });
    const [othersOpen] = useState(() => stack.size - 1);
    const floating = !expanded && !phone;
    const { geometry, handlers, reset, moving } = useWindowGeometry(kind, othersOpen, floating);
    const saves = useSaveSummary();
    const zNow = useRef(z);
    zNow.current = z;
    const toFront = useCallback(() => {
        if (stack.get(kind) === nextZ - 1) return;
        const top = nextZ++;
        stack.set(kind, top);
        setZ(top);
    }, [kind]);

    // Open without blocking the page, and put focus inside so the keyboard works there.
    useEffect(() => {
        const d = dialog.current;
        if (!d) return;
        if (!d.open) d.show();
        d.focus();
        stack.set(kind, zNow.current);
        return () => {
            stack.delete(kind);
            d.close();
        };
    }, [kind]);

    // Asked to the front (a link to what it shows, or opening another record of its kind): on top, focused.
    useEffect(() => {
        const onFront = (e: Event) => {
            if ((e as CustomEvent<string>).detail !== kind) return;
            toFront();
            dialog.current?.focus();
        };
        globalThis.addEventListener(FRONT_EVENT, onFront);
        return () => globalThis.removeEventListener(FRONT_EVENT, onFront);
    }, [kind, toFront]);

    // Asked to flash: to the front, and a ring pulses around it (again each time it's asked).
    const [flashing, setFlashing] = useState(0);
    useEffect(() => {
        const onFlash = (e: Event) => {
            if ((e as CustomEvent<string>).detail !== kind) return;
            toFront();
            dialog.current?.focus();
            setFlashing((n) => n + 1);
        };
        globalThis.addEventListener(FLASH_EVENT, onFlash);
        return () => globalThis.removeEventListener(FLASH_EVENT, onFlash);
    }, [kind, toFront]);
    useEffect(() => {
        if (!flashing) return;
        const t = setTimeout(() => setFlashing(0), 1100);
        return () => clearTimeout(t);
    }, [flashing]);

    // Escape closes (a non-blocking dialog doesn't do that by itself).
    useEffect(() => {
        const onKey = (e: KeyboardEvent) => {
            if (e.key !== "Escape" || e.defaultPrevented || !isTop(kind)) return;
            e.preventDefault();
            onClose();
        };
        document.addEventListener("keydown", onKey);
        return () => document.removeEventListener("keydown", onKey);
    }, [onClose, kind]);

    // Fill a phone screen.
    useEffect(() => {
        const onResize = () => setPhone(isPhone());
        globalThis.addEventListener("resize", onResize);
        return () => globalThis.removeEventListener("resize", onResize);
    }, []);

    return (
        <dialog
            ref={dialog}
            tabIndex={-1}
            aria-label={props.label ?? (typeof props.title === "string" ? props.title : undefined)}
            style={floating ? { zIndex: z, ...geometry } : { zIndex: z }}
            onPointerDownCapture={toFront}
            onFocusCapture={toFront}
            onClickCapture={keepWindowsOpen}
            className={clsx(
                // Fixed to the screen, above the page (and below menus opened from inside it). Clipped,
                // not hidden: the frame itself must never scroll (only its content does).
                "record-window fixed m-0 flex max-h-none max-w-none flex-col overflow-clip border border-line p-0 text-ink shadow-2xl outline-none transition-colors duration-150",
                // Being moved: the frame see-through, its contents more so (below), to see what's behind, like ServiceM8's windows.
                moving ? "bg-surface/20" : "bg-surface",
                props.tone && props.tone !== "blue" && `is-${props.tone}`,
                "max-md:inset-0 max-md:size-full max-md:rounded-none max-md:border-0",
                expanded ? "inset-4 h-auto w-auto rounded" : "rounded",
                // Alternating classes restart the animation when it's asked again mid-flash.
                flashing > 0 && (flashing % 2 ? "is-flashing" : "is-flashing-again"),
            )}
        >
            {/* Its own popups and questions cover this window's body only (Confirm.tsx WindowLayer): its title bar still moves it. */}
            <WindowLayer.Provider value={layer}>
            <ConfirmProvider inWindow>
                <header
                    className={clsx(
                        "flex shrink-0 touch-none items-center gap-2 bg-window px-3 py-1.5 text-white transition-opacity duration-150 select-none",
                        floating && "cursor-move",
                        moving && "opacity-20",
                    )}
                    {...handlers("move")}
                    onDoubleClick={(e) => !(e.target as HTMLElement).closest("button") && setExpanded((v) => !v)}
                >
                    {/* Centred, not on a baseline: a title with an icon (e.g. the sync badge) has none to line up with. */}
                    <h2 className="flex min-w-0 flex-1 items-center gap-2 text-[13px] font-semibold">
                        {props.icon ? <span className="shrink-0 text-white/90">{props.icon}</span> : null}
                        <span className="min-w-0 truncate">{props.title}</span>
                        {props.subtitle ? <span className="shrink-0 text-[11px] font-normal text-white/80">{props.subtitle}</span> : null}
                    </h2>
                    <SaveIndicator kind={saves.summary} />
                    {props.share ? <ShareButton kind={kind} onFailed={setShowLink} /> : null}
                    <TitleButton
                        label="Reset size and position"
                        onClick={() => {
                            setExpanded(false);
                            reset();
                        }}
                    >
                        <RotateCcw className="size-3.5" />
                    </TitleButton>
                    <TitleButton label={expanded ? "Make smaller" : "Expand"} onClick={() => setExpanded((v) => !v)}>
                        {expanded ? <Minimize2 className="size-3.5" /> : <Maximize2 className="size-3.5" />}
                    </TitleButton>
                    <button type="button" className="cursor-pointer rounded bg-white px-2.5 py-0.5 text-[11px] font-semibold text-window-ink hover:bg-white/90" onClick={onClose}>
                        Close
                    </button>
                </header>
                {showLink ? <LinkToCopy link={showLink} onDone={() => setShowLink(null)} /> : null}
                <SaveArea report={saves.report}>
                    <div ref={setLayer} className={clsx("relative flex min-h-0 flex-1 flex-col transition-opacity duration-150 md:flex-row", moving && "opacity-15")}>
                        <nav className="flex shrink-0 gap-1 overflow-x-auto border-b border-line bg-surface-2 p-1 md:w-16 md:flex-col md:border-r md:border-b-0" aria-label="Sections">
                            {props.tabs.map((t) => (
                                <button
                                    key={t.id}
                                    type="button"
                                    aria-current={t.id === props.tab ? "page" : undefined}
                                    onClick={() => props.onTab(t.id)}
                                    className={clsx(
                                        "flex shrink-0 cursor-pointer items-center gap-1.5 rounded border px-2.5 py-1.5 text-[11px] md:flex-col md:gap-0.5 md:px-0.5",
                                        t.id === props.tab ? "border-line bg-surface font-semibold text-ink shadow-sm" : "border-transparent text-muted hover:text-ink",
                                    )}
                                >
                                    <span className={clsx("relative", t.id === props.tab && "text-brand-600")}>
                                        {t.icon}
                                        {/* Not saved yet: green, the editing colour (as its Needs attention pill), not a warning's amber. */}
                                        {t.unsaved ? <span className="absolute -top-0.5 -right-1 size-2 rounded-full bg-brand-500 ring-2 ring-surface-2" title="Not saved yet" /> : null}
                                        {t.attention ? (
                                            <span
                                                className={clsx("absolute -right-1 -bottom-0.5 flex size-3 items-center justify-center rounded-full text-[8px] leading-none font-bold text-white ring-[1.5px] ring-surface-2", t.attention.danger ? "bg-danger" : "bg-warning")}
                                                title={t.attention.label}
                                            >
                                                !
                                            </span>
                                        ) : null}
                                        {t.locked ? (
                                            // Quiet: something to know (its price is locked), not something wrong.
                                            <span className="absolute -bottom-1 -left-1.5 flex size-3 items-center justify-center rounded-full bg-surface text-muted ring-1 ring-line" title={t.locked}>
                                                <Lock className="size-2" />
                                            </span>
                                        ) : null}
                                    </span>
                                    <span>
                                        {t.label}
                                        {t.unsaved ? <span className="sr-only"> (not saved yet)</span> : null}
                                        {t.locked ? <span className="sr-only"> ({t.locked})</span> : null}
                                        {t.attention ? <span className="sr-only"> ({t.attention.label})</span> : null}
                                    </span>
                                </button>
                            ))}
                            <MoreActions actions={(props.actions ?? []).filter((a): a is WindowAction => Boolean(a))} />
                        </nav>
                        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
                            {/* Notices about the whole record (archived, not synced...): pinned, they don't scroll away with a tab (at most 40% of the height, so a small window keeps room for it). */}
                            <div className="max-h-[40%] shrink-0 overflow-y-auto">{props.notices}</div>
                            {/* A column: what's at the end of a tab (mt-auto, e.g. an item's per-unit profit) sits at the bottom when the tab's short. */}
                            <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">{props.children}</div>
                        </div>
                    </div>
                </SaveArea>
                {floating ? (
                    <>
                        {/* Resize: the right edge, the bottom edge, and the corner. */}
                        <div aria-hidden="true" className="absolute top-0 right-0 bottom-3 w-1.5 cursor-ew-resize touch-none" {...handlers("right")} />
                        <div aria-hidden="true" className="absolute right-3 bottom-0 left-0 h-1.5 cursor-ns-resize touch-none" {...handlers("bottom")} />
                        <div aria-hidden="true" className="absolute right-0 bottom-0 size-3 cursor-nwse-resize touch-none" {...handlers("corner")} />
                    </>
                ) : null}
            </ConfirmProvider>
            </WindowLayer.Provider>
        </dialog>
    );
}

/** A small icon button on the title bar (not on phones, where the window fills the screen). */
/**
 * A link to the page you're on with just this window open: its record (the address key named for
 * its kind, e.g. ?item=<uuid>) and the tab it's on. Anyone signed in to the business opens it there.
 */
function windowLink(kind: string) {
    const here = new URLSearchParams(location.search);
    const keep = new URLSearchParams();
    for (const key of [kind, `${kind}Tab`]) {
        const value = here.get(key);
        if (value) keep.set(key, value);
    }
    return `${location.origin}${location.pathname}?${keep}`;
}

/** Copy link, in the title bar: says "Copied" for a moment, or (copying blocked, e.g. inside ServiceM8) shows the link to copy. */
function ShareButton({ kind, onFailed }: Readonly<{ kind: string; onFailed: (link: string) => void }>) {
    const [copied, setCopied] = useState(false);
    const copy = () => {
        const link = windowLink(kind);
        navigator.clipboard
            .writeText(link)
            .then(() => {
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => onFailed(link));
    };
    return (
        <TitleButton label={copied ? "Link copied" : "Copy a link to this"} onClick={copy} always>
            {copied ? <Check className="size-3.5" /> : <Link2 className="size-3.5" />}
        </TitleButton>
    );
}

/** The link to copy by hand, under the title bar, selected and ready. */
function LinkToCopy({ link, onDone }: Readonly<{ link: string; onDone: () => void }>) {
    return (
        <div className="flex shrink-0 items-center gap-2 border-b border-line bg-surface-2 px-3 py-1.5 text-xs">
            <span className="shrink-0 text-muted">Copy this link:</span>
            <input
                className="input min-w-0 flex-1 py-1 text-xs"
                readOnly
                value={link}
                aria-label="Link to this"
                ref={(el) => el?.select()}
                onFocus={(e) => e.target.select()}
            />
            <button type="button" className="rounded p-1 text-muted hover:text-ink" aria-label="Done" onClick={onDone}>
                <X className="size-3.5" />
            </button>
        </div>
    );
}

function TitleButton(props: Readonly<{ label: string; onClick: () => void; children: ReactNode; always?: boolean }>) {
    return (
        <button
            type="button"
            className={clsx(
                "size-6 cursor-pointer items-center justify-center rounded border border-white/40 hover:bg-white/15",
                props.always ? "inline-flex" : "hidden md:inline-flex",
            )}
            aria-label={props.label}
            title={props.label}
            onClick={props.onClick}
        >
            {props.children}
        </button>
    );
}

/** Something that can be done to a window's record: in its More menu, and asked first (or opening what asks, e.g. a popup). */
export interface WindowAction {
    icon: ReactNode;
    label: string;
    /** What it does, under its label in the menu. */
    help: string;
    /** The question asked first (Confirm.tsx), in the window. None: it opens its own (a popup asking what's needed). */
    confirm?: ConfirmOptions;
    /** Red in the menu: it removes or hides something (its question is then a danger too). */
    danger?: boolean;
    disabled?: boolean;
    run: () => void;
}

/**
 * A window's actions: a "More" button at the foot of its tabs (at the end of them on a phone),
 * opening a menu of them; choosing one asks first, in the window, then does it. Nothing without any.
 */
function MoreActions({ actions }: Readonly<{ actions: readonly WindowAction[] }>) {
    const confirm = useConfirm();
    if (!actions.length) return null;
    return (
        <div className="ml-auto shrink-0 md:mt-auto md:ml-0">
            <Popover
                label="More actions"
                width="w-72"
                beside
                buttonClassName="flex w-full cursor-pointer items-center gap-1.5 rounded border border-transparent px-2.5 py-1.5 text-[11px] text-muted hover:text-ink md:flex-col md:gap-0.5 md:px-0.5"
                button={
                    <>
                        <Ellipsis className="size-5" />
                        <span>More</span>
                    </>
                }
            >
                {(close) =>
                    actions.map((a) => (
                        <MenuItem
                            key={a.label}
                            icon={a.icon}
                            label={a.label}
                            help={a.help}
                            danger={a.danger}
                            disabled={a.disabled}
                            onClick={async () => {
                                close();
                                if (!a.confirm || (await confirm({ ...a.confirm, tone: a.danger ? "danger" : "primary" }))) a.run();
                            }}
                        />
                    ))
                }
            </Popover>
        </div>
    );
}
